import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import { format, isBefore, startOfMonth, endOfMonth, addMonths, isSameDay, eachDayOfInterval } from 'date-fns'
import { Spacer } from 'components/Utility'
import { SmallHeadline, MainHeadline, SubDescription } from 'components/Typography'
import { CautionButton, NavButton, OrangeButton, TextButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { useSelectWeekday } from 'hooks/form'
import { ScheduleCollectiveCalendarContainer } from 'components/Calendar'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { getHolidays } from 'reducks/store/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { getStoreRef } from 'reducks/auth/selectors'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { fetchBreads } from 'reducks/bread/operations'
import { saveCollectiveStocksConfirm } from 'reducks/confirm/operations'
import { BreadType } from 'types/bread'
import { CautionCard, ScheduleCollectiveCardList } from 'components/Card'
import { getBreads } from 'reducks/bread/selectors'
import { getConfirmCollectiveStocksBreads, getConfirmCollectiveStocksDates } from 'reducks/confirm/selectors'
import { SearchForm, SelectBox } from 'components/Form'
import { filterBreadsByCategory, filterBreadsByKeyword } from 'lib/filter'
import { useCategoryOptions, useFetchCategories } from 'hooks/category'
import { useSelectBreads } from 'hooks/bread'
const daysOfWeekInstance = new DaysOfWeek()

const ScheduleCollectiveEditTemplate: FC = () => {
  const dispatch = useDispatch()
  const type = 'preorder'
  const breads = useSelector((state: State) => getBreads(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const confirmBreads = useSelector((state: State) => getConfirmCollectiveStocksBreads(state))
  const confirmDates = useSelector((state: State) => getConfirmCollectiveStocksDates(state))

  // for fetchBread sortKey
  const categories = useFetchCategories()
  const sortKey = 'order'

  const [activeDates, setActiveDates] = useState<Date[]>([])
  const [businessDays, setBusinessDays] = useState<DayOfWeekType[]>([])
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeekType[]>([])
  const [categoryName, setCategoryName] = useState<string>('')
  const [keyword, setKeyword] = useState<string>('')
  const [breadList, setBreadList] = useState<BreadType[]>([])

  const handleCurrentMonth = useCallback((month: Date) => setCurrentMonth(month), [setCurrentMonth])
  const selectDaysOfWeek = useSelectWeekday(setDaysOfWeek)
  const selectAllDaysOfWeek = useCallback(() => setDaysOfWeek([...businessDays]), [setDaysOfWeek, businessDays])
  const unselectAllDaysOfWeek = useCallback(() => setDaysOfWeek([]), [setDaysOfWeek])

  const { selectBread, selectedBreads, selectAllBreads, setSelectedBreads, unselectAllBreads } =
    useSelectBreads(breadList)

  const selectDate = useCallback(
    (date: Date) => {
      // 過去の日付を追加しない、当日は追加できる
      if (isBefore(date, new Date()) && !isSameDay(date, new Date())) return

      setActiveDates((prevState) => {
        if (prevState.some((activeDate) => isSameDay(activeDate, date))) {
          return prevState.filter((activeDate) => !isSameDay(activeDate, date))
        } else {
          return [...prevState, date]
        }
      })
    },
    [setActiveDates]
  )

  // 選択した曜日で一括設定（上書き）する
  const selectAllDateByDaysOfWeek = useCallback(() => {
    // 当月の日付を全て取得する
    const datesInMonth = eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    })

    const dates: Date[] = []
    for (let i = 0; i < datesInMonth.length; i = (i + 1) | 0) {
      const date = datesInMonth[i]
      // 過去の日付であれば除外
      if (isBefore(date, new Date()) && !isSameDay(date, new Date())) continue
      // 休業日であれば除外
      if (closedDates.some((closedDate) => isSameDay(closedDate, date))) continue
      // 選択された曜日でなければ除外
      if (!daysOfWeek.includes(daysOfWeekInstance.getDayOfWeek(date, specialHolidays))) continue
      dates.push(date)
    }

    // 当月の受付設定を一括更新
    setActiveDates((prevState) => {
      const activeDatesInOtherMonths = prevState.filter((activeDate) => {
        return datesInMonth.every((date) => !isSameDay(date, activeDate))
      })
      return [...activeDatesInOtherMonths, ...dates]
    })
    setDaysOfWeek([])
  }, [daysOfWeek, closedDates, currentMonth, specialHolidays, setActiveDates, setDaysOfWeek])

  // 選択されたカテゴリーに応じてフィルタリング
  const selectCategoryName = useCallback(
    (value: string) => {
      if (value === '' && keyword === '') {
        // カテゴリーが未選択かつキーワードも未選択であれば初期値に戻す
        setBreadList([...breads])
      } else if (value === '' && keyword !== '') {
        // カテゴリーが未選択だがキーワードが入力されている場合
        const filteredBreadsByKeyword = filterBreadsByKeyword(keyword, breads)
        setBreadList([...filteredBreadsByKeyword])
      } else {
        // カテゴリでフィルターした後にキーワードでフィルタリング
        const filteredBreadsByCategory = filterBreadsByCategory(value, categories, breads)
        const filteredBreadsByKeyword = filterBreadsByKeyword(keyword, filteredBreadsByCategory)
        setBreadList([...filteredBreadsByKeyword])
      }
      setCategoryName(value)
    },
    [breads, categories, keyword, setCategoryName, setBreadList]
  )

  // 検索キーワードの入力
  const handleKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      // 空白であればカテゴリーのみのフィルタリング
      if (value === '') {
        const filteredBreadsByCategory = filterBreadsByCategory(categoryName, categories, breads)
        setBreadList([...filteredBreadsByCategory])
      }
      setKeyword(value)
    },
    [categoryName, categories, breads, setKeyword, setBreadList]
  )

  // キーワード検索(部分一致)
  const searchBreads = useCallback(
    (keyword: string) => {
      const filteredBreadsByCategory = filterBreadsByCategory(categoryName, categories, breads)
      const filteredBreadsByKeyword = filterBreadsByKeyword(keyword, filteredBreadsByCategory)
      setBreadList([...filteredBreadsByKeyword])
    },
    [breads, categories, keyword, setCategoryName, setBreadList]
  )

  // 事前予約商品を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public', sortKey, type))
  }, [dispatch, storeRef, sortKey, type])

  // 臨時休業日・営業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  // 臨時休業日を考慮して選択月の翌月まで休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    const startOfThisMonth = startOfMonth(new Date())
    dispatch(
      fetchClosedDates(
        startOfMonth(isBefore(startOfThisMonth, currentMonth) ? startOfThisMonth : currentMonth),
        endOfMonth(addMonths(currentMonth, 1)),
        holidays,
        specialHolidays,
        storeRef
      )
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  // 選択可能な曜日を判定
  useEffect(() => {
    const _businessDays = daysOfWeekInstance.getDaysOfWeek().filter((dayOfWeek) => !holidays.includes(dayOfWeek))
    setBusinessDays([..._businessDays])
  }, [holidays])

  // 確認画面から戻ってきた場合
  useEffect(() => {
    if (confirmBreads.length === 0) return
    setSelectedBreads([...confirmBreads])
  }, [confirmBreads])

  // 確認画面から戻ってきた場合
  useEffect(() => {
    if (confirmDates.length === 0) return
    setActiveDates([...confirmDates])
  }, [confirmDates])

  // ReduxのStoreにあるbreadsをローカルのbreadListに反映する
  useEffect(() => {
    let _breads: BreadType[] = [...breads]
    // カテゴリが指定されていればフィルタリング
    if (categoryName !== '') {
      _breads = filterBreadsByCategory(categoryName, categories, _breads)
    }
    // 検索キーワードが入力されていればフィルタリング
    if (keyword !== '') {
      _breads = filterBreadsByKeyword(keyword, _breads)
    }
    setBreadList([..._breads])
  }, [breads])

  // カテゴリの選択肢を生成
  const categoryOptions = useCategoryOptions(categories)

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'商品の受渡可能日の一括設定'} />
      <Spacer size={28} />
      <SmallHeadline label={'受渡可能日'} required={true} />
      <Spacer size={8} />
      <SubDescription
        description={
          'お客さまに受け渡しできる日をタップして選択してください。sacri上の定休日は、グレーで設定されています。'
        }
      />
      <p className={`${text.color_caution} ${text.font_small}`}>受渡可能日で選択されている月全体を上書きします。</p>
      <Spacer size={12} />
      <ScheduleCollectiveCalendarContainer
        activeDates={activeDates}
        businessDays={businessDays}
        currentMonth={currentMonth}
        daysOfWeek={daysOfWeek}
        handleCurrentMonth={handleCurrentMonth}
        selectAllDateByDaysOfWeek={selectAllDateByDaysOfWeek}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        selectDate={selectDate}
        selectDaysOfWeek={selectDaysOfWeek}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
      />
      <Spacer size={28} />
      <SmallHeadline label={'設定する商品'} />
      <Spacer size={8} />
      <SubDescription
        description={
          '登録中の受渡可能日は、個別日の設定も含めて上書きされます。また商品の販売予定にない営業日は、登録されませんのでご了承ください。'
        }
      />
      <Spacer size={20} />
      <SearchForm
        isFilled={keyword !== ''}
        onChange={handleKeyword}
        onClick={() => searchBreads(keyword)}
        placeholder={'商品名で検索する'}
        value={keyword}
      />
      <Spacer size={12} />
      <SelectBox options={categoryOptions} select={selectCategoryName} value={categoryName} />
      <Spacer size={28} />
      <SmallHeadline label={`${breadList.length}件の商品を表示中`} />
      <Spacer size={20} />
      {breadList.length === 0 ? (
        // キーワードやカテゴリでのフィルタリング結果が0件の場合
        <CautionCard
          align={'center'}
          caution={'該当商品がありません'}
          description={'別の方法で検索してみてください。'}
        />
      ) : (
        <>
          {selectedBreads.filter((selectedBread) =>
            breadList.some((bread) => bread.bread_ref?.id === selectedBread.bread_ref?.id)
          ).length > 0 ? (
            <TextButton label={'すべての商品のチェックを外す'} onClick={unselectAllBreads} />
          ) : (
            <TextButton label={'すべての商品にチェックを入れる'} onClick={selectAllBreads} />
          )}
          <Spacer size={12} />
          <ScheduleCollectiveCardList breads={breadList} selectedBreads={selectedBreads} selectBread={selectBread} />
          <Spacer size={48} />
          <OrangeButton
            disabled={activeDates.length === 0 || selectedBreads.length === 0}
            label={'設定内容を確認する'}
            onClick={() => dispatch(saveCollectiveStocksConfirm(selectedBreads, activeDates, undefined, type))}
            size={'medium'}
            type={'main'}
          />
          <Spacer size={12} />
          <CautionButton
            disabled={selectedBreads.length === 0}
            label={`${format(currentMonth, 'M月')}の受渡可能日を一括削除する`}
            onClick={() => dispatch(saveCollectiveStocksConfirm(selectedBreads, [], currentMonth, type))}
            size={'medium'}
            type={'outlined'}
          />
        </>
      )}
      <Spacer size={48} />
      <NavButton label={'事前予約受付設定に戻る'} iconPath={'/icon/grey/arrow_prev.svg'} url={'/schedule'} />
      <Spacer size={12} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default ScheduleCollectiveEditTemplate
